import React, { PropsWithChildren, useCallback } from 'react';
import { Nullable } from '@/ui-kit/types';
import { FormikProps } from 'formik';
import { Box, Button, Link, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useStyles } from '@/ui-kit/theme/styles';
import { useTimerDuration } from '@/bo-kit/components/VerifyAccountPage/components/VerifyAccountForm/hooks/useTimerDuration';
import liberBlack from '@/ui-kit/theme/colors/liberBlack';
import Clock from '@/bo-kit/components/VerifyAccountPage/components/VerifyAccountForm/images/clock-wait.svg';
import { Replay } from '@mui/icons-material';
import { GoBackLink } from '@/bo-kit/components/GoBackLink';

const RESEND_PERIOD = 60000;

export const EmailOTPCodeForm = <T extends { confirmationCode: string }>({
    email,
    formik,
    doResendCode,
    isError,
    isLoading,
    errorMessage,
    submitBtnCaption,
}: PropsWithChildren<{
    email: Nullable<string>;
    formik: FormikProps<T>;
    doResendCode: () => void;
    isError: boolean;
    isLoading: boolean;
    errorMessage: Nullable<string>;
    submitBtnCaption: string;
}>) => {
    const { spacing } = useTheme();
    const { t } = useTranslation('bo-kit__signup');
    const classes = useStyles();
    const { timer, isTimerEnd, resetTimer } = useTimerDuration(RESEND_PERIOD);
    const onResendClick = useCallback(() => {
        resetTimer();
        doResendCode();
    }, [doResendCode, resetTimer]);
    return (
        <Stack spacing={4}>
            {email ? (
                <div>
                    <Typography className={classes.commonText} variant='body2'>
                        {t('Please enter the confirmation code that was sent to:')}
                    </Typography>
                    <Typography color={liberBlack[200]} variant='body2'>
                        {email}
                    </Typography>
                </div>
            ) : (
                <Typography className={classes.commonText} variant='body2'>
                    {t('Please enter the confirmation code')}
                </Typography>
            )}
            <Stack spacing={1.25}>
                <TextField
                    fullWidth
                    type='text'
                    label={t('Confirmation code')}
                    name='confirmationCode'
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.confirmationCode}
                    error={
                        (formik.touched.confirmationCode && !!formik.errors.confirmationCode) ||
                        isError
                    }
                    helperText={
                        (formik.touched.confirmationCode &&
                            !!formik.errors.confirmationCode &&
                            String(formik.errors.confirmationCode)) ||
                        (isError && errorMessage)
                    }
                    placeholder={t('Enter the code')}
                    autoComplete='one-time-code'
                />
                {!isTimerEnd ? (
                    <Box sx={{ display: 'flex' }} alignItems={'center'}>
                        <img
                            style={{
                                width: spacing(3),
                                marginRight: spacing(1),
                            }}
                            alt='Clock img'
                            src={Clock}
                        />
                        <Typography color={liberBlack[200]} variant='body2'>
                            {t('Сan resend the code in {{timer}}', { timer })}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex' }} alignItems={'center'}>
                        <Link
                            component='button'
                            variant='body2'
                            onClick={onResendClick}
                            typography={{ variant: 'subtitle3', color: liberBlack[200] }}
                        >
                            <Replay
                                sx={({ spacing }) => ({
                                    position: 'relative',
                                    top: spacing(0.5),
                                    fontSize: spacing(2.5),
                                    mr: spacing(0.5),
                                    ml: spacing(0.5),
                                })}
                            />
                            {t('Resend the code')}
                        </Link>
                    </Box>
                )}
            </Stack>
            <div>
                <Button
                    type='submit'
                    variant='contained'
                    fullWidth
                    color='primary'
                    disabled={isLoading}
                >
                    {submitBtnCaption}
                </Button>
                <Box mt={1}>
                    <GoBackLink />
                </Box>
            </div>
        </Stack>
    );
};
